// src/helpers/ticketQrDecode.ts
// Giải mã nội dung QR quét được (URL đầy đủ in trên vé HOẶC token trần) thành thông tin vé
// {kỳ phát hành, số vé, ký hiệu}. Dùng chung cho mọi màn cần đọc QR vé (Giao vé, Thu hồi vé,
// Cổng tra cứu kết quả...).
//
// Ưu tiên xác thực chữ ký OFFLINE bằng public key (nhanh, không round-trip server) qua
// verifyAndDecodeTicketToken; chỉ gọi API backend cho định dạng token cũ (AES-256-GCM) mà xác thực
// offline trả về null.

import {
  extractTicketTokenFromScan,
  verifyAndDecodeTicketToken,
} from "./ticketTokenVerifier";

export interface ScannedTicket {
  lotteryPeriodId: number;
  ticketNumber: string;
  symbol: string;
  planCode: string;
}

// Các "ứng viên" token tách từ nội dung QR:
//  - Ứng viên chính = đoạn cuối path (extractTicketTokenFromScan).
//  - Bổ sung ứng viên BỎ tiền tố "v" cho vé in theo định dạng cũ, khi
//    QrGenerationJobConsumer.BuildQrUrl ghép thẳng "{lookupBaseUrl='.../v'}{token}" KHÔNG có "/"
//    ngăn cách -> ".../v{token}".
// Token luôn được xác thực chữ ký nên ứng viên sai chỉ đơn giản bị loại, không có rủi ro bảo mật.
export const ticketTokenCandidates = (rawScan: string): string[] => {
  const primary = extractTicketTokenFromScan(rawScan);
  const list = [primary];
  if (/^v[A-Za-z0-9_-]{20,}$/.test(primary)) list.push(primary.slice(1));
  return list.filter(Boolean);
};

export const decodeScannedTicket = async (
  rawScan: string,
  decodeTokenApi: (token: string) => Promise<any>,
): Promise<ScannedTicket | null> => {
  const candidates = ticketTokenCandidates(rawScan);
  if (candidates.length === 0) return null;

  // 1) Xác thực chữ ký offline (JWT RS256 / ECDSA nhị phân gọn).
  for (const token of candidates) {
    const offline = await verifyAndDecodeTicketToken(token);
    if (offline) {
      return {
        lotteryPeriodId: offline.lotteryPeriodId,
        ticketNumber: offline.ticketNumber,
        symbol: offline.symbol,
        planCode: offline.planCode,
      };
    }
  }

  // 2) Fallback: token định dạng cũ AES-256-GCM -> nhờ backend giải mã.
  for (const token of candidates) {
    try {
      const response: any = await decodeTokenApi(token);
      const payload = response?.data || response;
      if (payload?.ticketNumber && payload?.symbol) {
        return {
          lotteryPeriodId: Number(payload.lotteryPeriodId),
          ticketNumber: String(payload.ticketNumber),
          symbol: String(payload.symbol),
          planCode: payload.planCode ? String(payload.planCode) : "",
        };
      }
    } catch {
      /* thử ứng viên kế tiếp */
    }
  }

  return null;
};
